import { useState } from 'react';
import { useSEO, generateSchema } from '../../utils/seo';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { i18n } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoshino-village-project.com';

  useSEO({
    title: i18n.language === 'ja' ? 'お問い合わせ | Yoshino Village Project' : 'Contact | Yoshino Village Project',
    description: i18n.language === 'ja' 
      ? 'プロジェクトに関するご質問やご相談など、お気軽にお問い合わせください。吉野での地域共創、移住相談、参加方法についてお答えします。'
      : 'Feel free to contact us with any questions or inquiries about the project. We answer questions about regional co-creation in Yoshino, relocation consultation, and participation methods.',
    keywords: '吉野, お問い合わせ, 相談, 連絡先, 地域共創, 移住相談',
    canonical: `${siteUrl}/contact`,
    schema: generateSchema.webpage(
      `${siteUrl}/contact`,
      i18n.language === 'ja' ? 'お問い合わせ | Yoshino Village Project' : 'Contact | Yoshino Village Project',
      i18n.language === 'ja' 
        ? 'プロジェクトに関するご質問やご相談など、お気軽にお問い合わせください。吉野での地域共創、移住相談、参加方法についてお答えします。'
        : 'Feel free to contact us with any questions or inquiries about the project. We answer questions about regional co-creation in Yoshino, relocation consultation, and participation methods.'
    ),
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // バリデーション
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const subject = formData.get('subject') as string;
    const message = formData.get('message') as string;

    if (!name || !email || !subject || !message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (message.length > 500) {
      alert(i18n.language === 'ja' ? 'メッセージは500文字以内で入力してください。' : 'Please enter a message within 500 characters.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d559l3u1ia1uhnel1ie0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="pt-32 pb-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm text-gray-500 font-light mb-3 tracking-widest">CONTACT US</p>
            <h1 className="text-6xl font-black text-gray-900 mb-4">
              {i18n.language === 'ja' ? 'お問い合わせ' : 'Contact Us'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {i18n.language === 'ja' 
                ? 'プロジェクトに関するご質問やご相談など、お気軽にお問い合わせください'
                : 'Feel free to contact us with any questions or inquiries about the project'}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl p-10 shadow-xl"
            >
              <h2 className="text-3xl font-black text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-600 mb-8">
                {i18n.language === 'ja' ? 'お気軽にお問い合わせください' : 'Feel free to reach out to us'}
              </p>

              <form onSubmit={handleSubmit} id="contact-form" data-readdy-form>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {i18n.language === 'ja' ? 'お名前' : 'Name'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-700 transition-colors"
                      placeholder={i18n.language === 'ja' ? '山田 太郎' : 'John Doe'}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {i18n.language === 'ja' ? 'メールアドレス' : 'Email'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-700 transition-colors"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {i18n.language === 'ja' ? '件名' : 'Subject'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-700 transition-colors"
                      placeholder={i18n.language === 'ja' ? 'お問い合わせ内容' : 'Inquiry subject'}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {i18n.language === 'ja' ? 'メッセージ' : 'Message'} <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {i18n.language === 'ja' ? '(500文字以内)' : '(Max 500 characters)'}
                      </span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green-700 transition-colors resize-none"
                      placeholder={i18n.language === 'ja' ? 'お問い合わせ内容をご記入ください' : 'Please enter your message'}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green-800 text-white px-6 py-4 rounded-lg text-sm font-medium hover:bg-green-900 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (i18n.language === 'ja' ? '送信中...' : 'Sending...') : (i18n.language === 'ja' ? '送信する' : 'Send')}
                    <i className="ri-arrow-right-line"></i>
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-100 text-green-800 px-4 py-3 rounded-lg text-sm">
                      {i18n.language === 'ja' 
                        ? 'お問い合わせを受け付けました。ありがとうございます。'
                        : 'Your inquiry has been received. Thank you.'}
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-100 text-red-800 px-4 py-3 rounded-lg text-sm">
                      {i18n.language === 'ja' 
                        ? '送信に失敗しました。もう一度お試しください。'
                        : 'Submission failed. Please try again.'}
                    </div>
                  )}
                </div>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="rounded-3xl overflow-hidden shadow-xl h-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52428.89165641891!2d135.82366!3d34.36667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6006f8e3e3e3e3e3%3A0x3e3e3e3e3e3e3e3e!2z5ZCJ6YeO!5e0!3m2!1sja!2sjp!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '600px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Yoshino Location"
                ></iframe>
              </div>
              <div className="mt-6 bg-white rounded-2xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {i18n.language === 'ja' ? 'アクセス情報' : 'Access Information'}
                </h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-green-700 text-xl flex-shrink-0"></i>
                    <div>
                      <p className="font-medium text-gray-900">
                        {i18n.language === 'ja' ? '所在地' : 'Location'}
                      </p>
                      <p>{i18n.language === 'ja' ? '〒639-3115 奈良県吉野郡吉野町吉野山' : 'Yoshinoyama, Yoshino-cho, Yoshino-gun, Nara 639-3115'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-train-line text-green-700 text-xl flex-shrink-0"></i>
                    <div>
                      <p className="font-medium text-gray-900">
                        {i18n.language === 'ja' ? '電車でお越しの方' : 'By Train'}
                      </p>
                      <p>{i18n.language === 'ja' ? '近鉄吉野線「吉野駅」下車、ロープウェイで約3分' : 'Get off at Yoshino Station on the Kintetsu Yoshino Line, 3 minutes by ropeway'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-car-line text-green-700 text-xl flex-shrink-0"></i>
                    <div>
                      <p className="font-medium text-gray-900">
                        {i18n.language === 'ja' ? 'お車でお越しの方' : 'By Car'}
                      </p>
                      <p>{i18n.language === 'ja' ? '南阪奈道路「葛城IC」より約50分' : 'About 50 minutes from Katsuragi IC on Minami-Hanna Road'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;